import ModalLike from "components/modallike";
import { ReactComponent as Logo } from "assets/logo.svg";
import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material";
import OtpInput from "react-otp-input";
import styles from "./confirm.module.css";
import { ReactComponent as Success } from "assets/success.svg";
import { useAuth } from "hooks/useUser";
import axios, { AxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import config from "config";
import { object, string } from "yup";
import { Formik } from "formik";
import { useState } from "react";
import { Link } from "react-router-dom";

type Data = {
  otp: string;
};

async function verifyOTP(otp: Data) {
  const { data } = await axios.post("/auth/agent/verify", otp);
  return data;
}

const initialValues = {
  otp: "",
};

const validationSchema = object({
  otp: string().length(6).required(),
});

async function resendOTP() {
  const { data } = await axios.post("/auth/agent/resendotp");
  return data as {};
}

function ConfirmOTP() {
  const { data } = useAuth();
  const client = useQueryClient();

  const [isDisabled, setIsDisabled] = useState(false);

  const resend = useMutation(resendOTP, {
    onMutate() {
      setIsDisabled(true);
    },
    onSuccess() {
      setTimeout(() => setIsDisabled(false), 30_000);
    },
  });

  const submit = useMutation<User, AxiosError<{ message: string }>, Data>(verifyOTP, {
    onSuccess(data) {
      client.cancelQueries([config.userKey]);
      client.setQueryData([config.userKey], data);
      client.refetchQueries([config.userKey]);
    },
  });

  return (
    <ModalLike sx={{ textAlign: "center" }}>
      <Logo />
      <Box
        sx={{
          my: "86px",
        }}>
        <Typography variant="h5" fontWeight="bold" my="1rem" component="h1" mb={0}>
          Authentication
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          Please enter the 6-digit code sent to your email
          {data.email}
        </Typography>
        <Formik
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={(data) => submit.mutate(data)}>
          {({ handleSubmit, setFieldValue, values, errors }) => (
            <Box component="form" sx={{ mt: "1rem" }} onSubmit={handleSubmit}>
              <OtpInput
                hasErrored={(values.otp && !!errors.otp) || submit.isError}
                shouldAutoFocus
                value={values.otp}
                onChange={(data: string) => setFieldValue("otp", data)}
                containerStyle={{
                  justifyContent: "center",
                  gap: "12px",
                }}
                inputStyle={styles.input}
                numInputs={6}
                isInputNum
              />
              <Typography variant="caption" color="error">
                {(values.otp && errors.otp) || submit.error?.response?.data?.message || submit.error?.message}
              </Typography>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                sx={{
                  my: "1rem",
                  textTransform: "none",
                  fontWeight: "bold",
                }}>
                Submit
              </Button>
              <Typography variant="body2" color="textSecondary">
                Did not receive an OTP?{" "}
                <Typography
                  variant="body2"
                  disabled={isDisabled}
                  onClick={() => resend.mutate()}
                  color="primary"
                  type="button"
                  component={Button}
                  fontWeight="bold">
                  Resend
                </Typography>
              </Typography>
            </Box>
          )}
        </Formik>
      </Box>
      <Dialog open={data.otpVerified}>
        <DialogContent
          sx={{
            my: 7,
            textAlign: "center",
          }}>
          <Success />
          <Typography variant="h5" fontWeight="bold" my="1rem" component="h1" mb={0}>
            Welcome on board!
          </Typography>
          <Typography variant="body2" color="textSecondary" maxWidth="380px">
            Your Goloka account has been created. Set up your profile in 5 minutes
          </Typography>
          <Button
            component={Link}
            to="/setup"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{
              my: "1rem",
              textTransform: "none",
              fontWeight: "bold",
            }}>
            Proceed
          </Button>
        </DialogContent>
      </Dialog>
    </ModalLike>
  );
}

export default ConfirmOTP;
